import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useSelector } from 'react-redux';

import { initializeFlatfile } from '@flatfile/react';

import { Button } from '@mui/material';
import { Map as MapIcon } from '@mui/icons-material';

import { colors } from 'utils/constants';
import { contactSelectors } from 'store/selectors/contacts';

import { workbook } from './workbook';
import { spaceListener } from './listeners/simple';

import './style.css';

const StyledButton = styled(Button)`
  visibility: ${props => (props.hide ? 'hidden' : 'visible')};
`;

const { REACT_APP_FLATFILE_ENV_ID, REACT_APP_FLATFILE_PUBLISH_KEY } = process.env;

/**
 * "Import List" button that opens an embedded Flatfile import space in a
 * modal, themed to match the app and configured from the Flatfile settings in
 * the contacts store. Use on contact list screens (list index and detail) to
 * let users bulk-import contacts.
 */
const FlatSpaceButton = ({
  disabled,
  isLoading = false,
  hide = false,
  listUuid = null,
  onClick,
  visibility = 'visible',
}) => {
  const [showSpace, setShowSpace] = useState(false);
  const { flatFileConfig, flatFilePlatformConfig } = useSelector(state => ({
    flatFileConfig: contactSelectors.getFlatFileConfig(state),
    flatFilePlatformConfig: contactSelectors.getFlatFilePlatformConfig(state),
  }));

  const primaryButton = {
    ':hover': {
      backgroundColor: 'rgb(0, 116, 56)',
    },
    backgroundColor: colors.secondary,
    border: '0',
    borderRadius: '4px',
    color: '#fff',
  };

  const secondaryButton = {
    ':hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
    backgroundColor: 'transparent',
    border: '1px solid rgba(0, 0, 0, 0.23)',
    borderRadius: '4px',
    color: 'rgba(0, 0, 0, 0.87)',
  };

  const spaceProps = useMemo(
    () => ({
      closeSpace: {
        onClose: () => setShowSpace(false),
        operation: 'submitActionFg',
      },
      displayAsModal: true,
      environmentId: REACT_APP_FLATFILE_ENV_ID,
      listener: spaceListener(listUuid),
      name: 'Embedded Space',
      publishableKey: REACT_APP_FLATFILE_PUBLISH_KEY,
      sidebarConfig: {
        showSidebar: false,
      },
      themeConfig: {
        buttons: {
          headerMatchNo: {
            ...secondaryButton,
            padding: '6px 40px',
          },
          headerMatchYes: {
            ...primaryButton,
            padding: '6px 40px',
          },
          primary: primaryButton,
          secondary: secondaryButton,
          success: primaryButton,
          tertiary: secondaryButton,
        },
        global: {
          fontFamily: ['"Helvetica Neue"', 'sans-serif'].join(','),
          successColor: colors.secondary,
        },
      },
      workbook: workbook(flatFilePlatformConfig),
    }),
    [flatFileConfig, flatFilePlatformConfig]
  );

  const { Space, OpenEmbed } = initializeFlatfile(spaceProps);

  const onOpenSpace = async event => {
    if (onClick) {
      onClick(event);
    }

    setShowSpace(!showSpace);

    await OpenEmbed();
  };

  return (
    <>
      <StyledButton
        disabled={disabled || isLoading}
        hide={hide || visibility !== 'visible' ? 1 : 0}
        onClick={onOpenSpace}
        startIcon={<MapIcon />}
      >
        Import List
      </StyledButton>
      {showSpace && <Space />}
    </>
  );
};

FlatSpaceButton.propTypes = {
  disabled: PropTypes.bool,
  /** Hide the button via CSS visibility (it still occupies layout space). */
  hide: PropTypes.bool,
  isLoading: PropTypes.bool,
  /** Existing list to import contacts into; passed to the Flatfile space listener. */
  listUuid: PropTypes.string,
  /** Called with the click event just before the Flatfile space opens. */
  onClick: PropTypes.func,
  /** Any value other than 'visible' hides the button, same as `hide`. */
  visibility: PropTypes.string,
};

export default FlatSpaceButton;
